/*
 * Copyright © 2024 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschützt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulässig. Dies gilt
 * insbesondere für Vervielfältigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht übergeben, ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 */

import {afterNextRender, AfterViewInit, Directive, ElementRef, HostListener, inject, Input} from '@angular/core';
import {AbstractControl, NgControl} from '@angular/forms';
import {addError$, clearAll$, DvbError} from '@dv/shared/code';
import {document} from 'ngx-bootstrap/utils';
import {Click2EditComponent} from '../click-2-edit/click-2-edit.component';

@Directive({
    selector: '[dvlibClick2Edit]',
})
export class Click2EditDirective implements AfterViewInit {

    @Input({required: true, alias: 'dvlibClick2Edit'})
    public editable!: Click2EditComponent;

    private readonly ngControl = inject(NgControl, {self: true});
    private readonly elementRef = inject(ElementRef);

    private directiveControl: AbstractControl | null = null;
    private initValue: unknown;

    public constructor() {
        afterNextRender(() => {
            this.initValue = this.ngControl.control?.value;
            this.directiveControl = this.ngControl.control;
        });
    }

    /**
     * focus input element whenever it is initially rendered
     */
    public ngAfterViewInit(): void {
        setTimeout(() => this.focusFirstFocusableElementField());
    }

    @HostListener('keydown.enter', ['$event'])
    private onEnter(event: KeyboardEvent): void {
        const elementsRequiringEnterEvent = ['BUTTON', 'TEXTAREA'];

        if (event.target instanceof HTMLElement && elementsRequiringEnterEvent.includes(event.target.tagName)) {
            return;
        }

        this.confirmValue();
    }

    @HostListener('focusout', ['$event'])
    private onFocusOut(event: FocusEvent): void {
        if (this.elementRef.nativeElement.contains(event.relatedTarget)) {
            // when the focus moves from one contained element to another contained element, we don't submit
            return;
        }

        // hack: if datepicker overlay is opened, focusout does not mean we want to submit. But this is not
        // a good solution, because it doesn't consider att all if the datepicker was opened due to this
        // click-2-edit field
        if (document.querySelector('bs-datepicker-container')) {
            return;
        }

        this.confirmValue();
    }

    private confirmValue(): void {
        if (!this.directiveControl?.valid) {
            setTimeout(() => this.elementRef.nativeElement.focus());
            addError$.next(DvbError.validationError('ERRORS.ERR_INCOMPLETE_FORM', {}));

            this.editable.markAsSubmitted();

            return;
        }

        if (this.directiveControl?.value === this.initValue) {
            this.editable.cancelEdit();

            return;
        }

        this.editable.markAsSubmitted();

        // Came here, this means the form is valid, and we need to save the value
        clearAll$.next();
        this.editable.saveEdit();
        this.initValue = this.directiveControl?.value;
        // Calling blur to hide the input field when value was submitted by pressing enter
        setTimeout(() => this.elementRef.nativeElement.blur());
    }

    @HostListener('keydown.escape')
    @HostListener('keyup.escape')
    private leaveEditMode(): void {
        clearAll$.next();
        this.directiveControl?.setValue(this.initValue);
        setTimeout(() => this.elementRef.nativeElement.blur());
        this.editable.cancelEdit();
    }

    private focusFirstFocusableElementField(): void {
        const firstFocusableElement = this.findFirstFocusableField();
        firstFocusableElement?.focus();
    }

    private findFirstFocusableField(): HTMLElement | undefined {
        const focusableElementTags = ['input', 'textarea', 'select'];
        const nativeElement: HTMLElement = this.elementRef.nativeElement;

        if (focusableElementTags.includes(nativeElement.tagName.toLowerCase())) {
            return nativeElement;
        }

        // if the directive is bound to a component or another DOM element, search within for the 1st input field
        const focusableElements = nativeElement.querySelectorAll(focusableElementTags.join(','));
        const firstFocusableElement = focusableElements[0];

        if (firstFocusableElement instanceof HTMLElement) {
            return firstFocusableElement;
        }

        return undefined;
    }
}
